import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { toast } from "react-toastify";
import Seo from "../../components/Seo";
import { Avatar, FilePreview, PageHeader, PickerField, Spinner, revoke, withPreview } from "../../components/lawyer/LawyerUi";
import { apiErrorMessage, isBlankFile, lawyerService } from "../../services/lawyerApi";

const MAX_ATTACHMENTS = 4;

const toOptions = (arr = []) => arr.map((item) => ({ value: item.id, label: item.name }));
const idsOf = (list) => (Array.isArray(list) ? list.map((i) => i?.id).filter(Boolean) : []);
const languageNames = (value) => {
  if (!value) return [];
  const list = Array.isArray(value) ? value : String(value).split(",");
  return list.map((l) => (typeof l === "string" ? l.trim() : l?.name)).filter(Boolean);
};

const LawyerEditProfile = () => {
  const { profile, refreshProfile } = useOutletContext();
  const navigate = useNavigate();
  const loadedFor = useRef(null);
  const pictureRef = useRef(null);
  const certificateRef = useRef(null);
  const licenseRef = useRef(null);
  const attachmentRef = useRef(null);

  const [form, setForm] = useState({ name: "", email: "", phone: "", city: "", country: "", nationality: "", about: "" });
  const [languages, setLanguages] = useState([]);
  const [jurisdictions, setJurisdictions] = useState([]);
  const [arbitrators, setArbitrators] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [picture, setPicture] = useState(null);
  const [certificate, setCertificate] = useState(null);
  const [license, setLicense] = useState(null);
  const [attachments, setAttachments] = useState([]);

  const [lookups, setLookups] = useState({ countries: [], languages: [], jurisdictions: [], arbitrators: [], categories: [] });
  const [subCategoryOptions, setSubCategoryOptions] = useState([]);
  const [drafting, setDrafting] = useState(false);
  const [saving, setSaving] = useState(false);

  // Seed the form once the profile is available.
  useEffect(() => {
    if (!profile?.id || loadedFor.current === profile.id) return;
    loadedFor.current = profile.id;
    setForm({
      name: profile.name || "",
      email: profile.email || "",
      phone: profile.phone || "",
      city: profile.city || "",
      country: profile.country || "",
      nationality: profile.nationality || "",
      about: profile.about || "",
    });
    setLanguages(languageNames(profile.languages));
    setJurisdictions(idsOf(profile.jurisdictions));
    setArbitrators(idsOf(profile.arbitrators));
    setCategories(idsOf(profile.categories));
    setSubCategories(idsOf(profile.sub_categories));
    setCertificate(isBlankFile(profile.certificate) ? null : { src: profile.certificate });
    setLicense(isBlankFile(profile.license) ? null : { src: profile.license });
    setAttachments((profile.attachments || []).filter((u) => !isBlankFile(u)).map((src) => ({ src })));
  }, [profile]);

  useEffect(() => {
    let active = true;
    Promise.allSettled([lawyerService.getDropdownData(), lawyerService.getCategories()]).then(([dd, cats]) => {
      if (!active) return;
      const d = dd.status === "fulfilled" && dd.value?.data?.status ? dd.value.data.data : {};
      const c = cats.status === "fulfilled" && cats.value?.data?.status ? cats.value.data.data : [];
      const byName = (arr = []) => arr.map((i) => ({ value: i.name, label: i.name }));
      setLookups({
        countries: byName(d.countries),
        languages: byName(d.languages),
        jurisdictions: toOptions(d.jurisdictions),
        arbitrators: toOptions(d.arbitrators),
        categories: toOptions(Array.isArray(c) ? c : c?.categories),
      });
      if (dd.status === "rejected" || cats.status === "rejected") toast.error("Some options couldn't be loaded.");
    });
    return () => {
      active = false;
    };
  }, []);

  // Sub-categories depend on the chosen categories; drop picks that no longer apply.
  const categoryKey = categories.join(",");
  useEffect(() => {
    // Selections outside the loaded options are dropped on save, so the initial
    // empty render doesn't wipe the sub-categories seeded from the profile.
    if (!categories.length) {
      setSubCategoryOptions([]);
      return undefined;
    }
    let active = true;
    lawyerService
      .getSubCategories(categories)
      .then((res) => {
        if (!active || !res?.data?.status) return;
        const data = res.data.data;
        const list = Array.isArray(data) ? data : data?.sub_categories || [];
        const valid = new Set(list.map((i) => i.id));
        setSubCategoryOptions(toOptions(list));
        setSubCategories((prev) => prev.filter((id) => valid.has(id)));
      })
      .catch(() => active && toast.error("Couldn't load sub categories."));
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryKey]);

  // Release preview URLs of picked files when leaving the page.
  const filesRef = useRef([]);
  filesRef.current = [picture, certificate, license, ...attachments];
  useEffect(() => () => filesRef.current.forEach(revoke), []);

  const countryOptions = useMemo(() => {
    // Keep a saved value selectable even if it isn't in the lookup list.
    const extra = [form.country, form.nationality].filter(
      (v) => v && !lookups.countries.some((o) => o.value === v)
    );
    return [...lookups.countries, ...[...new Set(extra)].map((v) => ({ value: v, label: v }))];
  }, [lookups.countries, form.country, form.nationality]);

  const languageOptions = useMemo(() => {
    const extra = languages.filter((v) => !lookups.languages.some((o) => o.value === v));
    return [...lookups.languages, ...extra.map((v) => ({ value: v, label: v }))];
  }, [lookups.languages, languages]);

  if (!profile?.email) {
    return (
      <div className="lwp-page lwp-page-narrow">
        <PageHeader title="Edit Profile" backTo="/lawyer/profile" />
        <Spinner />
      </div>
    );
  }

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const setValue = (key) => (v) => setForm((f) => ({ ...f, [key]: v }));

  const pickFile = (setter, { imageOnly } = {}) => (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (imageOnly && !file.type.startsWith("image/")) return toast.error("Please choose an image.");
    setter((prev) => {
      revoke(prev);
      return withPreview(file);
    });
  };

  const addAttachments = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    const room = MAX_ATTACHMENTS - attachments.length;
    if (files.length > room) toast.info(`You can add up to ${MAX_ATTACHMENTS} attachments.`);
    setAttachments((prev) => [...prev, ...files.slice(0, room).map(withPreview)]);
  };

  const removeAttachment = (index) =>
    setAttachments((prev) => {
      revoke(prev[index]);
      return prev.filter((_, i) => i !== index);
    });

  const labelsFor = (options, ids) =>
    ids.map((id) => options.find((o) => o.value === id)?.label).filter(Boolean).join(", ");

  const writeWithAI = async () => {
    const text = form.about.trim();
    if (text.length < 10) return toast.error("Write a few words about yourself first, then let AI polish it.");
    setDrafting(true);
    try {
      const res = await lawyerService.draftBioWithAI(
        text,
        labelsFor(lookups.categories, categories),
        labelsFor(lookups.jurisdictions, jurisdictions)
      );
      if (!res?.data?.status) throw new Error(res?.data?.message);
      setForm((f) => ({ ...f, about: res.data.data.suggestion }));
      toast.success("Draft ready. Review and edit it before saving.");
    } catch (err) {
      toast.error(apiErrorMessage(err, err?.message || "The AI assistant is unavailable right now."));
    } finally {
      setDrafting(false);
    }
  };

  const save = async () => {
    const name = form.name.trim();
    const email = form.email.trim();
    if (!name) return toast.error("Full name is required.");
    if (!/^\S+@\S+\.\S+$/.test(email)) return toast.error("Enter a valid email address.");
    if (!form.phone.trim()) return toast.error("Phone number is required.");
    if (!languages.length) return toast.error("Select at least one language.");
    if (!jurisdictions.length) return toast.error("Select at least one jurisdiction.");
    if (!categories.length) return toast.error("Select at least one category.");
    if (!certificate) return toast.error("Please upload your certificate.");
    if (!license) return toast.error("Please upload your legal license.");

    const validSubCategories = !categories.length
      ? []
      : subCategoryOptions.length
      ? subCategories.filter((id) => subCategoryOptions.some((o) => o.value === id))
      : subCategories;

    setSaving(true);
    try {
      const res = await lawyerService.updateProfileRequest({
        name,
        email,
        phone: form.phone.trim(),
        city: form.city.trim(),
        country: form.country,
        nationality: form.nationality,
        about: form.about.trim(),
        languages: JSON.stringify(languages),
        jurisdictions: JSON.stringify(jurisdictions),
        arbitrators: JSON.stringify(arbitrators),
        categories: JSON.stringify(categories),
        sub_categories: JSON.stringify(validSubCategories),
        picture: picture?.file,
        certificate: certificate.file,
        license: license.file,
        attachments: attachments.map((a) => a.file || a.src),
      });
      if (!res?.data?.status) throw new Error(res?.data?.message);
      toast.success(res.data.message || "Profile updated");
      await refreshProfile();
      navigate("/lawyer/profile");
    } catch (err) {
      toast.error(apiErrorMessage(err, err?.message || "Unable to update your profile."));
    } finally {
      setSaving(false);
    }
  };

  const attachmentSlots = Array.from({ length: Math.max(MAX_ATTACHMENTS, attachments.length) }, (_, i) => attachments[i] || null);

  return (
    <div className="lwp-page lwp-page-narrow lwp-edit-profile">
      <Seo title="Edit Profile | Legal Platform" noindex />
      <PageHeader
        title="Edit Profile"
        backTo="/lawyer/profile"
        actions={
          <button type="button" className="lwp-pill-btn lwp-save-btn" onClick={save} disabled={saving}>
            {saving ? <span className="spinner-border spinner-border-sm" /> : "Save"}
          </button>
        }
      />

      <button type="button" className="lwp-edit-avatar" onClick={() => pictureRef.current?.click()} aria-label="Change profile picture">
        <Avatar src={picture?.src || profile.picture} alt={profile.name} size={120} />
        <span className="lwp-edit-avatar-cam">
          <i className="bi bi-camera-fill" />
        </span>
      </button>
      <input ref={pictureRef} type="file" accept="image/*" hidden onChange={pickFile(setPicture, { imageOnly: true })} />

      <section className="lwp-card lwp-form lwp-edit-card">
        <label className="lwp-field">
          <span>Full Name</span>
          <input className="lwp-input lwp-edit-input" value={form.name} onChange={set("name")} autoComplete="name" />
        </label>
        <label className="lwp-field">
          <span>Email</span>
          <input className="lwp-input lwp-edit-input" type="email" value={form.email} onChange={set("email")} autoComplete="email" />
        </label>
        <label className="lwp-field">
          <span>Phone Number</span>
          <input className="lwp-input lwp-edit-input" type="tel" value={form.phone} onChange={set("phone")} autoComplete="tel" />
        </label>
        <label className="lwp-field">
          <span>City</span>
          <input className="lwp-input lwp-edit-input" value={form.city} onChange={set("city")} autoComplete="address-level2" />
        </label>

        <PickerField label="Country" options={countryOptions} value={form.country} onChange={setValue("country")} />
        <PickerField label="Nationality" options={countryOptions} value={form.nationality} onChange={setValue("nationality")} />
        <PickerField label="Language" multi options={languageOptions} value={languages} onChange={setLanguages} />
        <PickerField label="Select Jurisdiction" multi options={lookups.jurisdictions} value={jurisdictions} onChange={setJurisdictions} />
        <PickerField label="Legal Consultant" multi options={lookups.arbitrators} value={arbitrators} onChange={setArbitrators} />
        <PickerField label="Select Categories" multi options={lookups.categories} value={categories} onChange={setCategories} />
        <PickerField
          label="Select Sub Categories"
          multi
          options={subCategoryOptions}
          value={subCategories}
          onChange={setSubCategories}
          placeholder={categories.length ? "Select" : "Select categories first"}
          disabled={!categories.length}
        />

        <label className="lwp-field">
          <span>About Your Self</span>
          <textarea className="lwp-textarea lwp-edit-textarea" value={form.about} onChange={set("about")} rows={4} />
        </label>
        <div>
          <button type="button" className="lwp-ai-btn" onClick={writeWithAI} disabled={drafting}>
            {drafting ? <span className="spinner-border spinner-border-sm" /> : <i className="bi bi-stars" />}
            Write with AI
          </button>
        </div>

        <div className="lwp-edit-docs">
          <button type="button" className="lwp-edit-doc" onClick={() => certificateRef.current?.click()}>
            <span className="lwp-edit-doc-preview">
              <FilePreview item={certificate} label="Certificate" />
            </span>
            Certificate*
          </button>
          <button type="button" className="lwp-edit-doc" onClick={() => licenseRef.current?.click()}>
            <span className="lwp-edit-doc-preview">
              <FilePreview item={license} label="Legal License" />
            </span>
            Legal License*
          </button>
        </div>
        <input ref={certificateRef} type="file" accept="image/*,application/pdf" hidden onChange={pickFile(setCertificate)} />
        <input ref={licenseRef} type="file" accept="image/*,application/pdf" hidden onChange={pickFile(setLicense)} />

        <div className="lwp-edit-attachments">
          {attachmentSlots.map((item, i) => (
            <div key={item?.src || `slot-${i}`} className="lwp-edit-attachment">
              {item ? (
                <>
                  <FilePreview item={item} label={`Attachment ${i + 1}`} />
                  <button type="button" className="lwp-edit-attachment-x" onClick={() => removeAttachment(i)} aria-label="Remove attachment">
                    <i className="bi bi-x-lg" />
                  </button>
                </>
              ) : (
                <button type="button" className="lwp-edit-attachment-add" onClick={() => attachmentRef.current?.click()} aria-label="Add attachment">
                  <i className="bi bi-plus-lg" />
                </button>
              )}
            </div>
          ))}
        </div>
        <input ref={attachmentRef} type="file" accept="image/*,application/pdf" multiple hidden onChange={addAttachments} />
      </section>
    </div>
  );
};

export default LawyerEditProfile;
